import { useEffect, useState } from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { formatDateLong } from '@/lib/format';
import { useTranslation } from 'react-i18next';

export default function LicenseExpiredOverlay() {
  const { t } = useTranslation();
  const [expired, setExpired] = useState(false);
  const [planName, setPlanName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    // Only check if user is authenticated
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    checkLicense();
    const interval = setInterval(() => {
      const t = localStorage.getItem('accessToken');
      if (t) checkLicense();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkLicense = async () => {
    try {
      const res = await api.get('/license');
      const data = res.data?.data;
      if (!data) return;

      if (data.expired === true || (data.expires_at && new Date(data.expires_at) < new Date())) {
        setExpired(true);
        setPlanName(data.plan || '');
        setExpiresAt(data.expires_at ? formatDateLong(data.expires_at) : '');
      } else {
        setExpired(false);
      }
    } catch {
      // Don't block on error
    }
  };

  if (!expired) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-white">
      <div className="max-w-lg w-full mx-4 text-center">
        {/* Icon */}
        <div className="mx-auto w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mb-8">
          <Shield className="w-10 h-10 text-red-500" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">
          {t('common:x2.licenseExpired', 'Ihre Lizenz ist abgelaufen')}
        </h1>

        {/* Description */}
        <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-md mx-auto">
          {t('common:x2.yourVyndeskLicense', 'Ihre Vyndesk-Lizenz ist am')} <span className="font-medium text-slate-700">{expiresAt}</span> abgelaufen.
          Um den Zugang zu Ihrem Helpdesk-System wiederherzustellen, verlängern Sie bitte Ihre Lizenz.
        </p>

        {/* Info Card */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mb-8 text-left">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('common:x2.whatDoesThatMean', 'Was bedeutet das?')}</h3>
              <ul className="text-xs text-slate-500 space-y-1.5">
                <li>• Ihre Daten sind sicher gespeichert und gehen nicht verloren.</li>
                <li>• Neue Tickets werden weiterhin empfangen, können aber nicht bearbeitet werden.</li>
                <li>• Nach der Verlängerung haben Sie sofort wieder vollen Zugriff.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <a
            href="https://app.vyndesk.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F99FD] text-white text-sm font-semibold rounded-lg hover:bg-[#0c84e0] transition-colors"
          >
            <Shield className="w-4 h-4" />
            {t('common:x2.manageLicense', 'Lizenz verwalten')}
          </a>
          <p className="text-xs text-slate-400">
            {t('common:x2.redirectToLicenseMgmt', 'Sie werden zur Lizenzverwaltung weitergeleitet.')}
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-100">
          <p className="text-[10px] text-slate-300 uppercase tracking-widest">
            {t('common:x2.vyndeskHelpdesk', 'Vyndesk Helpdesk System')}
          </p>
        </div>
      </div>
    </div>
  );
}
